import { Children, cloneElement, isValidElement, ReactElement, ReactNode } from 'react';
import { InputValue, normalize } from '@optimus-web/common';
import { DropdownItemProps } from '../../DropdownItem';
import { OptionGroup, OptionGroupProps } from '../components/dropdown/OptionGroup';
import { SelectProps } from '../types';

type RequiredSelectProps = Required<Pick<SelectProps, 'children' | 'searchValueMapper' | 'valueMapper' | 'selected'>>;

type OptionalSelectProps = Pick<SelectProps, 'onSearchChange' | 'customSearch' | 'readOnly' | 'multiselect' | 'name'>;

interface AdditionalHookProps {
    searchValue: string;
    selectedIndex: number;
    indexChangeReason?: 'user' | 'internal';
    keyboardHandled: boolean;
    selectAll: boolean;
    handleItemClick: (e: React.BaseSyntheticEvent, val: InputValue, index?: number) => void;
    setKeyboardHandled: (handled: boolean) => void;
    setSelectedIndex: (index: number) => void;
    setIndexChangeReason: (reason: 'user' | 'internal') => void;
}

type UseFilteredItemsProps = RequiredSelectProps & OptionalSelectProps & AdditionalHookProps;

export const useFilteredItems = ({
    children,
    searchValue,
    onSearchChange,
    customSearch,
    searchValueMapper,
    valueMapper,
    readOnly,
    multiselect,
    selectAll,
    selected,
    selectedIndex,
    indexChangeReason,
    keyboardHandled,
    handleItemClick,
    name,
    setKeyboardHandled,
    setSelectedIndex,
    setIndexChangeReason,
}: UseFilteredItemsProps) => {
    const normalizedSearchValue = normalize(searchValue);

    const flattenedItems = flattenChildren(children);

    const groupsWithMatches = new Set<string>();
    flattenedItems.forEach((c: ReactElement<DropdownItemProps>) => {
        if (!isValidElement(c) || isOptionGroup(c)) return;

        const matchesSearch =
            !normalizedSearchValue.length ||
            onSearchChange ||
            (customSearch
                ? customSearch(c, normalizedSearchValue)
                : (searchValueMapper(c) || valueMapper(c)?.toString())?.includes(normalizedSearchValue));

        if (matchesSearch && c.props.groupId) {
            groupsWithMatches.add(c.props.groupId);
        }
    });

    const filtered = flattenedItems.filter((c) => {
        if (!isValidElement(c)) return false;

        if (isOptionGroup(c)) {
            return c.props.groupId ? groupsWithMatches.has(c.props.groupId) : false;
        }

        // In readOnly mode, only show selected items
        if (readOnly) {
            const val = valueMapper(c);
            return multiselect ? (selected as InputValue[]).includes(val) : selected === val;
        }

        // Normal filtering logic for non-readOnly mode
        if (!normalizedSearchValue.length || onSearchChange) return true;
        if (customSearch) {
            return customSearch(c, normalizedSearchValue);
        }
        const childSearchValue = searchValueMapper(c) || valueMapper(c)?.toString();
        return childSearchValue ? childSearchValue.includes(normalizedSearchValue) : false;
    });

    const isSelected = (c: ReactElement) => {
        const val = valueMapper(c);
        return multiselect ? (selected as InputValue[]).includes(val) : selected === val;
    };

    return filtered.map((c, index) => {
        // For group headers, just return them as is
        if (isOptionGroup(c)) {
            return c;
        }

        // Regular items
        const effectiveIndex = selectAll ? index + 1 : index;
        const isFocused = effectiveIndex === selectedIndex;

        const commonProps = {
            multiselect: readOnly ? false : multiselect,
            focused: isFocused,
            shouldScrollIntoView: indexChangeReason === 'user' && keyboardHandled,
            keyboardHandled,
            selected: readOnly ? false : c.props.selected || isSelected(c),
            onClick: readOnly
                ? undefined
                : c.props.onClick ||
                  ((e: React.BaseSyntheticEvent, value: InputValue) => handleItemClick(e, value, index)),
            onMouseEnter: readOnly
                ? undefined
                : () => {
                      setKeyboardHandled(false);
                      setSelectedIndex(effectiveIndex);
                      setIndexChangeReason('user');
                  },
            tabIndex: -1,
            role: 'option',
            id: `${name}-${c.props.value}`,
            disabled: readOnly || c.props.disabled,
            'aria-selected': c.props.selected || isSelected(c),
            readOnly,
        };

        // Include group-related aria attributes if the item belongs to a group
        if (c.props.groupId) {
            return cloneElement(c, {
                ...commonProps,
                key: c.props.value,
                'aria-describedby': `${c.props.groupId}-label`,
            });
        }

        return cloneElement(c, {
            ...commonProps,
            key: c.props.value,
        });
    });
};

export const isOptionGroup = (element: ReactElement): element is ReactElement<OptionGroupProps> =>
    typeof element.type !== 'string' && element.type === OptionGroup;

// Helper function to flatten nested children
export const flattenChildren = (children: ReactNode): ReactElement[] => {
    const flattened: ReactElement[] = [];

    Children.forEach(children, (child) => {
        if (!isValidElement(child)) return;

        if (isOptionGroup(child)) {
            flattened.push(
                cloneElement(child, {
                    ...child.props,
                    key: `group-${child.props.groupId}`,
                    children: undefined,
                })
            );

            const groupChildren = flattenChildren(child.props.children);

            groupChildren.forEach((groupChild) => {
                flattened.push(
                    cloneElement(groupChild, {
                        ...groupChild.props,
                        key: groupChild.props.value,
                        groupId: child.props.groupId,
                    })
                );
            });
        } else {
            // For regular dropdown items, add them directly
            flattened.push(child);
        }
    });

    return flattened;
};
